import { create } from 'zustand';
import { getUserAssets, getUserFolders, getAllUserFolders, ensureGeneralFolder, getVideosWithPendingResources, UserAsset, UserAssetFolder } from '@/services/data/userAssets';
import { getVideoContent, VideoContent } from '@/services/data/videoContent';

export type VideoWithPendingResources = {
  id: string;
  title: string | null;
  status: string | null;
  hasResources: boolean;
  earliestPublicationDate: Date | null;
};

type FolderView = 'root' | 'general' | 'video' | 'archive';

interface UserAssetsState {
  // Data
  assets: UserAsset[];
  folders: UserAssetFolder[];
  allFolders: UserAssetFolder[];
  videosWithPendingResources: VideoWithPendingResources[];
  videoContents: VideoContent[];
  generalFolderId: string | null;

  // Navigation
  currentView: FolderView;
  currentVideoContent: VideoContent | null;
  currentFolderId: string | null;
  folderPath: UserAssetFolder[];

  // Loading state
  isLoading: boolean;

  // Actions
  loadInitialData: (userId: string) => Promise<void>;
  loadAllFolders: (userId: string) => Promise<void>;
  loadContent: (userId: string, folderId: string | null) => Promise<void>;
  refreshPendingResources: (userId: string) => Promise<void>;
  
  // Navigation actions
  setCurrentView: (view: FolderView) => void;
  setCurrentVideoContent: (video: VideoContent | null) => void;
  setCurrentFolderId: (folderId: string | null) => void;
  setFolderPath: (path: UserAssetFolder[]) => void;
  navigateToFolder: (folder: UserAssetFolder) => void;
  navigateBack: () => void;
  resetNavigation: () => void;

  // Data update actions
  addAsset: (asset: UserAsset) => void;
  updateAsset: (assetId: string, asset: UserAsset) => void;
  removeAsset: (assetId: string) => void;
  addFolder: (folder: UserAssetFolder) => void;
  updateFolder: (folderId: string, folder: UserAssetFolder) => void;
  removeFolder: (folderId: string) => void;
  updateVideoContent: (videoId: string, video: VideoContent) => void;
  updateVideoContents: (videos: VideoContent[]) => void;
}

export const useUserAssetsStore = create<UserAssetsState>((set, get) => ({
  // Initial state
  assets: [],
  folders: [],
  allFolders: [],
  videosWithPendingResources: [],
  videoContents: [],
  generalFolderId: null,
  currentView: 'root',
  currentVideoContent: null,
  currentFolderId: null,
  folderPath: [],
  isLoading: false,

  // Data loading
  loadInitialData: async (userId: string) => {
    set({ isLoading: true });
    try {
      const [videoResult, generalFolder, pendingResources] = await Promise.all([
        getVideoContent(userId).catch(error => {
          console.error('Error fetching video content:', error);
          return { success: false as const, error };
        }),
        ensureGeneralFolder(userId).catch(error => {
          console.error('Error ensuring General folder:', error);
          return null;
        }),
        getVideosWithPendingResources(userId).catch(error => {
          console.error('Error fetching videos with pending resources:', error);
          return [];
        })
      ]);

      if (videoResult.success && videoResult.data) {
        set({ videoContents: videoResult.data });
      } else if (videoResult.error) {
        console.error('Failed to load video content:', videoResult.error);
      }

      if (generalFolder) {
        set({ generalFolderId: generalFolder.id });
      }

      set({ videosWithPendingResources: pendingResources });

      await get().loadAllFolders(userId);
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      set({ isLoading: false });
    }
  },

  loadAllFolders: async (userId: string) => {
    try {
      const folders = await getAllUserFolders(userId);
      set({ allFolders: folders });
    } catch (error) {
      console.error('Error loading all folders:', error);
    }
  },

  loadContent: async (userId: string, folderId: string | null) => {
    const { currentView } = get();
    
    // If we're in video view but have no folder ID, don't load root folders
    // This prevents showing other videos' folders
    if (currentView === 'video' && (folderId === null || folderId === '__video_no_folder__')) {
      set({ folders: [], assets: [], isLoading: false });
      return;
    }
    
    set({ isLoading: true });
    try {
      const [foldersData, assetsData] = await Promise.all([
        getUserFolders(userId, folderId),
        getUserAssets(userId, folderId)
      ]);
      set({ folders: foldersData, assets: assetsData });
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      set({ isLoading: false });
    }
  },

  refreshPendingResources: async (userId: string) => {
    try {
      const pendingResources = await getVideosWithPendingResources(userId);
      set({ videosWithPendingResources: pendingResources });
    } catch (error) {
      console.error('Error refreshing pending resources:', error);
    }
  },

  // Navigation actions
  setCurrentView: (view: FolderView) => {
    set({ currentView: view });
  },

  setCurrentVideoContent: (video: VideoContent | null) => {
    set({ currentVideoContent: video });
  },

  setCurrentFolderId: (folderId: string | null) => {
    set({ currentFolderId: folderId });
  },

  setFolderPath: (path: UserAssetFolder[]) => {
    set({ folderPath: path });
  },

  navigateToFolder: (folder: UserAssetFolder) => {
    const { folderPath } = get();
    set({
      folderPath: [...folderPath, folder],
      currentFolderId: folder.id
    });
  },

  navigateBack: () => {
    const { folderPath, currentView, generalFolderId, currentVideoContent, allFolders } = get();
    
    if (folderPath.length === 0) {
      // Back to root of current view
      if (currentView === 'general' && generalFolderId) {
        set({ currentFolderId: generalFolderId });
      } else if (currentView === 'video' && currentVideoContent) {
        const videoFolder = allFolders.find(
          f => f.name === (currentVideoContent.title || `Vidéo ${currentVideoContent.id.slice(0, 8)}`)
        );
        set({ currentFolderId: videoFolder?.id || null });
      }
    } else {
      const newPath = [...folderPath];
      newPath.pop();
      set({ folderPath: newPath });
      
      if (newPath.length > 0) {
        set({ currentFolderId: newPath[newPath.length - 1].id });
      } else {
        // Back to the main folder (General or Video root)
        if (currentView === 'general' && generalFolderId) {
          set({ currentFolderId: generalFolderId });
        } else if (currentView === 'video' && currentVideoContent) {
          const videoFolder = allFolders.find(
            f => f.name === (currentVideoContent.title || `Vidéo ${currentVideoContent.id.slice(0, 8)}`)
          );
          set({ currentFolderId: videoFolder?.id || null });
        }
      }
    }
  },

  resetNavigation: () => {
    set({
      currentView: 'root',
      currentFolderId: null,
      currentVideoContent: null,
      folderPath: []
    });
  },

  // Data update actions
  addAsset: (asset: UserAsset) => {
    set(state => ({ assets: [...state.assets, asset] }));
  },

  updateAsset: (assetId: string, asset: UserAsset) => {
    set(state => ({
      assets: state.assets.map(a => a.id === assetId ? asset : a)
    }));
  },

  removeAsset: (assetId: string) => {
    set(state => ({ assets: state.assets.filter(a => a.id !== assetId) }));
  },

  addFolder: (folder: UserAssetFolder) => {
    set(state => ({ folders: [...state.folders, folder] }));
  },

  updateFolder: (folderId: string, folder: UserAssetFolder) => {
    set(state => ({
      folders: state.folders.map(f => f.id === folderId ? folder : f),
      folderPath: state.folderPath.map(f => f.id === folderId ? folder : f)
    }));
  },

  removeFolder: (folderId: string) => {
    set(state => ({ folders: state.folders.filter(f => f.id !== folderId) }));
  },

  updateVideoContent: (videoId: string, video: VideoContent) => {
    set(state => ({
      videoContents: state.videoContents.map(v => v.id === videoId ? video : v),
      currentVideoContent: state.currentVideoContent?.id === videoId ? video : state.currentVideoContent
    }));
  },

  updateVideoContents: (videos: VideoContent[]) => {
    set({ videoContents: videos });
  },
}));
